// list of customers// customers base page
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlanNameBadge from '../../components/common/PlanNameBadge';
import ExpiryTypo from '../../components/common/ExpiryTypo';
import ContactCell from '../../components/common/ContactCell';
import SubscriptionStatusFilter from '../../components/common/SubscriptionStatusFilter';
import TableSearchComponent from '../../components/common/TableSearchComponent';
import { useCustomers } from '../../context/CustomersContext';
import { formatDate } from '../../utils/helper';
import api from '../../services/api';

const CustomerList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [deactivateDialog, setDeactivateDialog] = useState({
    open: false,
    account: null,
    loading: false,
    remarks: '',
    error: ''
  });
  const [actionsMenu, setActionsMenu] = useState({
    anchorEl: null,
    account: null
  });

  // Get customers data from context
  const { customers, loading, error, fetchCustomers } = useCustomers();

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSubscriptionFilter = (value) => {
    setSubscriptionFilter(value);
  };

  const handleRefresh = () => {
    fetchCustomers();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully',
      severity: 'success'
    });
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleEditCustomer = (customerId) => {
    navigate(`/customers/${customerId}/edit`);
  };

  const openActionsMenu = (event, customer) => {
    event.stopPropagation();
    setActionsMenu({
      anchorEl: event.currentTarget,
      account: customer
    });
  };

  const closeActionsMenu = () => {
    setActionsMenu({
      anchorEl: null,
      account: null
    });
  };

  const openDeactivateDialog = (customer) => {
    setDeactivateDialog({
      open: true,
      account: customer,
      loading: false,
      remarks: '',
      error: ''
    });
  };

  const closeDeactivateDialog = () => {
    if (deactivateDialog.loading) return;
    setDeactivateDialog({
      open: false,
      account: null,
      loading: false,
      remarks: '',
      error: ''
    });
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateDialog.account) return;

    if (!deactivateDialog.remarks.trim()) {
      setDeactivateDialog(prev => ({
        ...prev,
        error: 'Remarks are required'
      }));
      return;
    }

    setDeactivateDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await api.post(`/account/${deactivateDialog.account.id}/deactivate/`, {
        remarks: deactivateDialog.remarks.trim()
      });
      await fetchCustomers();
      setSnackbar({
        open: true,
        message: response.data?.message || 'Account deactivated successfully',
        severity: 'success'
      });
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to deactivate account';
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    } finally {
      setDeactivateDialog({
        open: false,
        account: null,
        loading: false,
        remarks: '',
        error: ''
      });
    }
  };





  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.industry.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Subscription status and plan filtering logic
    const remainingDays = customer.subscriptionExpiry?.remainingDays || 0;
    const matchesSubscription = (() => {
      switch (subscriptionFilter) {
        case 'all':
          return true;
        case 'active':
          return remainingDays > 7;
        case 'expired':
          return remainingDays < 0;
        case 'expiring_soon':
          return remainingDays >= 0 && remainingDays <= 7;
        case 'free':
          return customer.subscriptionPlan.toLowerCase().includes('free');
        case 'basic':
          return customer.subscriptionPlan.toLowerCase().includes('basic');
        case 'advance':
          return customer.subscriptionPlan.toLowerCase().includes('advance');
        case 'premium':
          return customer.subscriptionPlan.toLowerCase().includes('premium');
        default:
          return true;
      }
    })();
    
    // AND logic between different filter types
    return matchesSearch && matchesSubscription;
  });

  return (
    <Box sx={{ px: 3, pt: 0, pb: 3 }}>
      {/* Remove or reduce the extra margin below this Box to collapse vertical space */}
      {/* <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
      <Box sx={{ mb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Customer Management
        </Typography>
      </Box>
      {/* Reduce margin below filter Card or remove altogether if not needed */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={8}>
              <TableSearchComponent
                value={searchTerm}
                onChange={handleSearch}
                columns={['company name', 'owner', 'email', 'industry']}
                size="medium"
              />
            </Grid>

            {/* Subscription Filter */}
            <Grid item xs={12} md={4}>
              <SubscriptionStatusFilter
                value={subscriptionFilter}
                onChange={handleSubscriptionFilter}
                size="medium"
                fullWidth
              />
            </Grid>


          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading customers...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No customers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                  <TableRow 
                    key={customer.id} 
                    onClick={() => handleViewCustomer(customer.id)} 
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {customer.company_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.industry}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ContactCell
                        name={customer.ownerName}
                        email={customer.email}
                        phone={customer.phone}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <PlanNameBadge plan={customer.subscriptionPlan} name={customer.subscriptionName} />
                        {(() => {
                          const remainingDays = customer.subscriptionExpiry?.remainingDays || 0;
                          const expiryDate = customer.subscriptionExpiry?.expiry;
                          
                          return (
                            <>
                              {remainingDays > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  {remainingDays} days remaining
                                </Typography>
                              )}
                              <ExpiryTypo
                                dateString={expiryDate}
                                remainingDays={remainingDays}
                              />
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {customer.totalUsers} / {customer.maxUsersAllowed} users
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round((customer.totalUsers / customer.maxUsersAllowed) * 100)}% usage
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{customer.lastLogin}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) => openActionsMenu(event, customer)}
                        aria-controls={actionsMenu.anchorEl ? 'customer-actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={actionsMenu.anchorEl ? 'true' : undefined}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu
        id="customer-actions-menu"
        anchorEl={actionsMenu.anchorEl}
        open={Boolean(actionsMenu.anchorEl)}
        onClose={closeActionsMenu}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem
          disabled={actionsMenu.account?.status === 'DC'}
          onClick={(event) => {
            event.stopPropagation();
            const targetAccount = actionsMenu.account;
            closeActionsMenu();
            if (targetAccount) {
              openDeactivateDialog(targetAccount);
            }
          }}
        >
          Deactivate Account
        </MenuItem>
      </Menu>

      <Dialog
        open={deactivateDialog.open}
        onClose={closeDeactivateDialog}
      >
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to deactivate{' '}
            <strong>{deactivateDialog.account?.company_name}</strong>? This will
            soft delete all users under this account and prevent future logins.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            multiline
            minRows={3}
            label="Remarks"
            placeholder="Add remarks for deactivation"
            value={deactivateDialog.remarks}
            onChange={(event) =>
              setDeactivateDialog(prev => ({
                ...prev,
                remarks: event.target.value,
                error: ''
              }))
            }
            error={Boolean(deactivateDialog.error)}
            helperText={deactivateDialog.error}
            disabled={deactivateDialog.loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog} disabled={deactivateDialog.loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            color="error"
            variant="contained"
            disabled={deactivateDialog.loading}
          >
            {deactivateDialog.loading ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerList;